"use client";

import { EdgeFade } from "@/components/edge-fade";
import { Container } from "@/components/layout/container";
import { TextShimmer } from "@/components/text-shimmer";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { BLUR_DATA_URL } from "@/config/site";
import { encodedHotelParams } from "@/feature/hotel/search/lib/hotel-formatter-helper";
import { addDays } from "date-fns";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useCallback, useMemo, useState, useTransition } from "react";

type DestinationCard = {
  id: number;
  title: string;
  description: string;
  img: string;
  region: string;
  hotel: {
    location: {
      id: number;
      name: string;
      code: number;
      type: "Hotel" | "City";
      city_code: number;
      country_code: string;
      country_name: string;
      star_category?: number;
    };
  };
};

const data: DestinationCard[] = [
  {
    id: 1732268,
    title: "Hotel Sacher Vienna",
    description: "Iconic luxury stay in central Vienna with timeless elegance.",
    img: "https://images.unsplash.com/photo-1516550893923-42d28e5677af?auto=format&fit=crop&w=1200&q=80",
    region: "AUSTRIA",
    hotel: {
      location: {
        id: 1732268,
        name: "Hotel Sacher Vienna",
        code: 1052180,
        type: "Hotel",
        city_code: 156857,
        country_code: "AT",
        country_name: "Austria",
        star_category: 4,
      },
    },
  },
  {
    id: 1732302,
    title: "Grand Ferdinand Vienna",
    description: "Stylish rooftop pool stay on Vienna's famous Ringstrasse.",
    img: "https://images.unsplash.com/photo-1527668752968-14dc70a27c95?auto=format&fit=crop&w=1200&q=80",
    region: "AUSTRIA",
    hotel: {
      location: {
        id: 1732302,
        name: "Grand Ferdinand Vienna",
        code: 2032792,
        type: "Hotel",
        city_code: 156857,
        country_code: "AT",
        country_name: "Austria",
        star_category: 4,
      },
    },
  },
  {
    id: 1732318,
    title: "Altstadt Vienna",
    description: "Boutique art hotel with cozy suites in Vienna's old town.",
    img: "https://images.unsplash.com/photo-1473448912268-2022ce9509d8?auto=format&fit=crop&w=1200&q=80",
    region: "AUSTRIA",
    hotel: {
      location: {
        id: 1732318,
        name: "Altstadt Vienna",
        code: 1052221,
        type: "Hotel",
        city_code: 156857,
        country_code: "AT",
        country_name: "Austria",
        star_category: 3,
      },
    },
  },
  {
    id: 1756553,
    title: "Aqua Dome Tirol Therme",
    description: "Alpine spa retreat with thermal pools and mountain views.",
    img: "https://images.unsplash.com/photo-1521295121783-8a321d551ad2?auto=format&fit=crop&w=1200&q=80",
    region: "AUSTRIA",
    hotel: {
      location: {
        id: 1756553,
        name: "Aqua Dome Tirol Therme",
        code: 1107627,
        type: "Hotel",
        city_code: 122118,
        country_code: "AT",
        country_name: "Austria",
        star_category: 3,
      },
    },
  },
  {
    id: 1696763,
    title: "Schloss Fuschl Resort",
    description: "Lakeside castle retreat with refined Austrian luxury.",
    img: "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?auto=format&fit=crop&w=1200&q=80",
    region: "AUSTRIA",
    hotel: {
      location: {
        id: 1696763,
        name: "Schloss Fuschl Resort",
        code: 1847157,
        type: "Hotel",
        city_code: 121449,
        country_code: "AT",
        country_name: "Austria",
        star_category: 5,
      },
    },
  },
  {
    id: 2150089,
    title: "Das Triest Vienna",
    description: "Contemporary design hotel with peaceful courtyard dining.",
    img: "https://images.unsplash.com/photo-1512918728675-ed5a9ecdebfd?auto=format&fit=crop&w=1200&q=80",
    region: "AUSTRIA",
    hotel: {
      location: {
        id: 2150089,
        name: "Das Triest Vienna",
        code: 1700161,
        type: "Hotel",
        city_code: 121673,
        country_code: "AT",
        country_name: "Austria",
        star_category: 5,
      },
    },
  },
];

function buildRegions(items: DestinationCard[]) {
  const counts = items.reduce<Record<string, number>>((acc, item) => {
    const key = item.region || item.hotel.location.country_name || "Other";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const list = Object.entries(counts).map(([name, count]) => ({ name, count }));
  return [{ name: "All", count: items.length }, ...list];
}

const regionsStatic = buildRegions(data);

export const TopDestination = () => {
  const router = useRouter();
  const [activeRegion, setActiveRegion] = useState("All");
  const [activeCardId, setActiveCardId] = useState<number | null>(null);
  const [isPending, startTransition] = useTransition();

  const filteredData = useMemo(() => {
    if (activeRegion === "All") return data;
    return data.filter((item) => item.region === activeRegion);
  }, [activeRegion]);
  const defaultSearchDates = useMemo(
    () => ({
      from: addDays(new Date(), 3),
      to: addDays(new Date(), 6),
    }),
    [],
  );

  const getHotelHref = useCallback(
    (item: DestinationCard) => {
      const params = encodedHotelParams({
        date: defaultSearchDates,
        location: item.hotel.location,
        rooms: [
          {
            adults: 2,
            children: 0,
            infants: 0,
            children_ages: [],
          },
        ],
      });

      return `/hotels?${params}`;
    },
    [defaultSearchDates],
  );

  const handleSearchHotel = useCallback(
    (item: DestinationCard) => {
      if (isPending) return;

      const href = getHotelHref(item);
      setActiveCardId(item.id);

      startTransition(() => {
        router.push(href);
      });
    },
    [getHotelHref, isPending, router],
  );

  return (
    <section className="relative ">
      {/* Premium SVG decorative shapes */}
      <svg
        className="absolute top-10 right-[5%] w-10 h-10 text-primary/10"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <polygon points="12,2 22,12 12,22 2,12" />
      </svg>

      <svg
        className="absolute top-1/3 left-[3%] w-8 h-8 text-sky-500/15 animate-pulse"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M12,0 L14,10 L24,12 L14,14 L12,24 L10,14 L0,12 L10,10 Z" />
      </svg>

      <svg
        className="absolute bottom-20 right-[8%] w-14 h-14 text-primary/10"
        viewBox="0 0 64 64"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
        strokeDasharray="4 4"
        aria-hidden="true"
      >
        <circle cx="32" cy="32" r="28" />
      </svg>

      <svg
        className="absolute bottom-1/4 left-[6%] w-6 h-6 text-violet-500/15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
        aria-hidden="true"
      >
        <polygon points="12,2 22,8 22,16 12,22 2,16 2,8" />
      </svg>

      {/* Gradient accent lines */}
      <div className="absolute top-0 right-1/4 w-px h-20 bg-linear-to-b from-primary/15 to-transparent" />
      <div className="absolute bottom-0 left-1/3 w-px h-16 bg-linear-to-t from-sky-500/10 to-transparent" />

      {/* Glowing dots */}
      <div className="absolute top-24 right-[12%] w-2 h-2 bg-primary/25 rounded-full shadow-lg shadow-primary/15" />
      <div className="absolute bottom-32 left-[10%] w-1.5 h-1.5 bg-sky-500/35 rounded-full" />

      <Container className="mt-10">
        <h2 className="text-3xl font-bold mb-2">Trending Stays</h2>
        <p className="mb-4 max-w-2xl text-muted-foreground">
          Discover our top trending stays, handpicked for unforgettable
        </p>
      </Container>
      <Container>
        <div className="mb-12" />

        <div className="flex flex-col lg:flex-row gap-8  ">
          {/* Left */}
          <div className="flex flex-col space-y-6 mb-8 lg:w-[28%] lg:mb-0">
            <div>
              <p className="text-muted-foreground italic text-base leading-relaxed mb-8 border-l-4 border-chart-3 pl-4 max-w-sm bg-chart-3/5 dark:bg-chart-3/10 rounded-r-lg py-3">
                &ldquo;Escape to the world&apos;s most exquisite sanctuaries,
                where every detail defines luxury.&rdquo;
                <span className="block text-xs font-bold uppercase tracking-widest mt-2 not-italic text-primary dark:text-cyan-400">
                  — G360 Tours & Travels
                </span>
              </p>

              <div className="grid grid-cols-2 gap-y-3 gap-x-4">
                {regionsStatic.map((region) => (
                  <div
                    key={region.name}
                    onClick={() => setActiveRegion(region.name)}
                    className={`flex items-center justify-between font-bold cursor-pointer transition-all duration-200 group text-sm ${
                      activeRegion === region.name
                        ? "text-foreground translate-x-1"
                        : "text-muted-foreground hover:text-foreground/80"
                    }`}
                  >
                    <span className="uppercase tracking-wide">
                      {region.name}
                    </span>

                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full transition-colors ${
                        activeRegion === region.name
                          ? "bg-primary/10 text-primary"
                          : "bg-muted text-muted-foreground group-hover:bg-muted/80"
                      }`}
                    >
                      {region.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right */}
          <div className="relative flex items-center lg:w-[72%]">
            <Carousel
              opts={{ align: "start", loop: true }}
              className="h-full w-full px-2 relative "
            >
              <EdgeFade />
              <CarouselContent className="">
                {filteredData.map((item) => {
                  const isCardLoading = isPending && activeCardId === item.id;

                  return (
                    <CarouselItem
                      key={item.id}
                      className="h-auto pl-3 basis-full sm:basis-1/2 lg:basis-1/3 xl:basis-1/4"
                    >
                      <button
                        type="button"
                        onClick={() => handleSearchHotel(item)}
                        aria-busy={isCardLoading}
                        disabled={isPending}
                        className={`relative group h-95 w-full overflow-hidden bg-muted text-left transition-opacity ${
                          isPending
                            ? "cursor-not-allowed pointer-events-none opacity-85"
                            : "cursor-pointer"
                        }`}
                      >
                        <div className="absolute inset-0">
                          <Image
                            src={item.img}
                            alt={item.title}
                            fill
                            sizes="(min-width: 1280px) 25vw, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                            className="object-cover transition-transform duration-700 group-hover:scale-110"
                            quality={70}
                            placeholder="blur"
                            blurDataURL={BLUR_DATA_URL}
                            loading="lazy"
                          />

                          {/* overlay: theme-safe "dark" */}
                          <div className="absolute inset-0 bg-[hsl(var(--foreground)/0.20)] dark:bg-[hsl(var(--background)/0.25)]" />

                          {/* gradient: theme-safe "dark" */}
                          <div className="absolute inset-0 bg-linear-to-t from-[hsl(var(--foreground)/0.92)] via-[hsl(var(--foreground)/0.45)] to-transparent dark:from-[hsl(var(--background)/0.92)] dark:via-[hsl(var(--background)/0.45)]" />
                        </div>

                        {isCardLoading && (
                          <div className="absolute inset-0 z-30 grid place-items-center bg-black/40 backdrop-blur-[1px]">
                            <div className="rounded-full border border-white/30 bg-white/10 px-3 py-1 text-xs font-semibold text-white">
                              <TextShimmer duration={1.2}>
                                Searching hotels...
                              </TextShimmer>
                            </div>
                          </div>
                        )}

                        {/* Title on image */}
                        <div className="absolute bottom-6 left-6 z-10 transition-all duration-300 group-hover:opacity-0 group-hover:translate-y-4">
                          <h3 className="text-2xl font-bold drop-shadow-md leading-tight max-w-[90%] font-nunito text-amber-100">
                            {item.title}
                          </h3>
                          <p className="text-xs mt-1 font-medium text-amber-100/80">
                            {item.hotel?.location?.country_name || "Country"}
                          </p>
                        </div>

                        {/* Bottom drawer (primary) */}
                        <div className="absolute inset-x-0 bottom-0 z-20 flex flex-col justify-end bg-primary p-6 text-primary-foreground shadow-[0_-5px_20px_hsl(var(--foreground)/0.15)] translate-y-[101%] transition-transform duration-500 ease-in-out group-hover:translate-y-0">
                          <h4 className="text-lg font-bold font-nunito mb-1">
                            {item.title}
                          </h4>

                          <span className="text-xs mb-3 block opacity-90">
                            {item.hotel?.location?.country_name || "Country"}
                          </span>

                          <p className="text-xs leading-relaxed line-clamp-3 mb-4 opacity-90">
                            {item.description}
                          </p>

                          <div className="flex items-center justify-between border-t border-primary-foreground/15 pt-3">
                            <span className="text-xs font-bold uppercase tracking-wider">
                              Search Hotels &rarr;
                            </span>
                          </div>
                        </div>
                      </button>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>

              <CarouselPrevious
                variant="ghost"
                size="icon"
                className="hidden md:flex left-4 md:-left-4 z-40 size-12 rounded-full bg-primary text-primary-foreground shadow-2xl hover:bg-primary/90 transition-all active:scale-90 [&_svg]:size-6"
              />
              <CarouselNext
                variant="ghost"
                size="icon"
                className="hidden md:flex right-4 md:-right-4 z-40 size-12 rounded-full bg-primary text-primary-foreground shadow-2xl hover:bg-primary/90 transition-all active:scale-90 [&_svg]:size-6"
              />
            </Carousel>
          </div>
        </div>
      </Container>
    </section>
  );
};
